import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Button,
  Checkbox,
  Indicators,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
  Textarea,
} from 'ui'
import CreatableSelect from 'react-select/creatable'
import { useUpdateProfileMutation } from '../../../queries/useUpdateProfileMutation'
import { useCurrentUser } from '../../../queries/useCurrentUser'
import { useAdminProfileById } from '../../../queries/useAdminProfileById'
import { useCompanies } from '../../../queries/useCompanies'
import { useJobs } from '../../../queries/useJobs'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { useSkipOnboarding } from '../../../hooks'
import { formatPhoneNumber } from 'utils'
import { useQueryClient } from '@tanstack/react-query'

// Validation schema
const schema = yup.object({
  companyName: yup.string().required('Cannot be blank'),
  firstName: yup.string().required('Cannot be blank'),
  lastName: yup.string().required('Cannot be blank'),
  jobField: yup.string().required('Cannot be blank'),
  phone: yup.string().min(10, 'Must be a valid phone number'),
  secondaryLanguages: yup
    .array()
    .of(yup.string().required())
    .optional()
    .nullable()
    .default([]),
  about: yup.string().required(),
})
export type ProfessionalProfilePart1 = yup.InferType<typeof schema>

interface ProfessionalStepOneProps {
  onSuccess?: () => void
  hideNavigation?: boolean
  editMode?: boolean
  profileId?: string // Added profileId parameter
  targetField?: string | null // Added targetField parameter for highlighting
}

const FormComponent: React.FC<ProfessionalStepOneProps> = ({
  onSuccess,
  hideNavigation = false,
  editMode = false,
  profileId,
  targetField,
}) => {
  // Use admin profile query if profileId is provided, otherwise use current user
  const { data: user, isLoading: isCurrentUserLoading } = useCurrentUser()
  const {
    data: adminProfile,
    isLoading: isAdminProfileLoading,
  } = useAdminProfileById(profileId)

  // Determine which profile data to use
  const profileData = profileId ? adminProfile : user
  const isLoading = profileId ? isAdminProfileLoading : isCurrentUserLoading

  const navigate = useNavigate()

  // Track which fields have been interacted with to remove highlighting
  const [interactedFields, setInteractedFields] = useState<Set<string>>(
    new Set()
  )

  const form = useForm<ProfessionalProfilePart1>({
    defaultValues: {
      companyName: '',
      firstName: '',
      lastName: '',
      jobField: '',
      phone: '',
      secondaryLanguages: [],
      about: '',
    } as ProfessionalProfilePart1,
    reValidateMode: 'onChange',
    resolver: yupResolver(schema as any) as any,
  })

  useEffect(() => {
    if (profileData) {
      form.setValue('firstName', profileData?.firstName as string)
      form.setValue('lastName', profileData?.lastName as string)
      form.setValue('jobField', profileData?.jobField as string)
      form.setValue('phone', profileData?.phone as string)
      form.setValue('about', profileData?.about as string)
      form.setValue(
        'secondaryLanguages',
        profileData?.secondaryLanguages as string[]
      )
      form.setValue('companyName', profileData?.companyName as string)
    }
  }, [profileData])

  const [userAddedCompany, setUserAddedCompany] = useState(
    null as string | null
  )
  const { data: companies } = useCompanies()
  if (userAddedCompany) {
    companies?.push(userAddedCompany)
  }
  const selectCompany = (company: string) => {
    form.setValue('companyName', company)
  }

  const [userAddedJob, setUserAddedJob] = useState(null as string | null)
  const { data: jobs } = useJobs()
  if (userAddedJob) {
    jobs?.push(userAddedJob)
  }
  const selectJob = (job: string) => {
    form.setValue('jobField', job)
  }

  const { mutateAsync: updateProfile } = useUpdateProfileMutation()

  const queryClient = useQueryClient()
  const handleSubmitForm = (redirect?: string) => async (
    values: ProfessionalProfilePart1
  ) => {
    await updateProfile({
      // Use the appropriate userId based on whether we're editing a specific profile
      userId: profileId || (user?.userId as string),
      companyName: values.companyName as string,
      jobField: values.jobField as string,
      phone: values.phone as string,
      firstName: values.firstName as string,
      lastName: values.lastName as string,
      secondaryLanguages: (values.secondaryLanguages as string[]) ?? [],
      about: values.about as string,
    })
    queryClient.invalidateQueries({ queryKey: ['currentUser'] })

    // Call onSuccess callback if provided
    if (onSuccess) {
      onSuccess()
    }

    // Only navigate if redirect is provided and we're not in edit mode
    if (redirect && !editMode) {
      navigate(redirect)
    }
  }
  const skipOnboarding = useSkipOnboarding()

  const onSaveDraft = async () => {
    // Try to save current form data as best-effort, but always skip regardless of validation
    const values = form.getValues()
    try {
      await handleSubmitForm(undefined)(values)
    } catch {
      // Ignore errors - we're skipping anyway
    }
    skipOnboarding()
  }

  const languages = ['Spanish', 'French', 'Latin', 'German', 'Arabic', 'Other']

  // Helper function to get highlighting classes for targeted fields
  const getFieldHighlightClasses = (fieldName: string) => {
    if (targetField === fieldName && !interactedFields.has(fieldName)) {
      return 'animate-pulse bg-yellow-50 shadow-lg shadow-yellow-400/50 ring-4 ring-yellow-300 ring-opacity-75 rounded-lg border border-yellow-400 p-3 -m-3'
    }
    return ''
  }

  // Helper function to mark a field as interacted with
  const markFieldAsInteracted = (fieldName: string) => {
    setInteractedFields((prev) => new Set([...prev, fieldName]))
  }

  // Scroll to highlighted field when component mounts with targetField
  useEffect(() => {
    if (targetField && !isLoading) {
      const timer = setTimeout(() => {
        const fieldElement = document.getElementById(`field-${targetField}`)
        if (fieldElement) {
          fieldElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })

          // Try to focus a traditional input/textarea within the field
          const inputElement = fieldElement.querySelector('input, textarea')
          if (
            inputElement instanceof HTMLInputElement ||
            inputElement instanceof HTMLTextAreaElement
          ) {
            inputElement.focus()
          }
        }
      }, 500) // Small delay to ensure rendering is complete

      return () => clearTimeout(timer)
    }
  }, [targetField, isLoading])

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(
          handleSubmitForm(editMode ? undefined : '/onboard/professional/two')
        )}
        className={clsx(isLoading && 'opacity-50')}
      >
        <FormLabel>Tell Us Who You Are and Where You Work</FormLabel>
        <div className="flex flex-col sm:grid sm:grid-cols-4 md:grid-cols-12 gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="col-span-3">
                <FormLabel className="hidden">First Name</FormLabel>
                <FormControl>
                  <Input placeholder="First Name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="col-span-6">
                <FormLabel className="hidden">Last Name</FormLabel>
                <FormControl>
                  <Input placeholder="Last Name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem className="w-full col-span-3">
                <FormLabel className="hidden">Phone Number</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Phone"
                    {...field}
                    onBlur={(e) => {
                      const formattedValue = formatPhoneNumber(e.target.value)
                      field.onChange(formattedValue)
                      field.onBlur()
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="md:grid md:grid-cols-2 gap-4 mt-2">
          <FormField
            control={form.control}
            name="companyName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="hidden">Employer</FormLabel>
                <FormControl>
                  <CreatableSelect
                    placeholder="Name of Your Employer"
                    options={companies?.map((company) => ({
                      value: company,
                      label: company,
                    }))}
                    value={
                      companies
                        ?.filter((company: string) => company === field.value)
                        .map((company: string) => ({
                          value: company,
                          label: company,
                        }))[0]
                    }
                    onChange={(option) => {
                      field.onChange(option?.value)
                      if (option) {
                        selectCompany(option.value)
                      }
                    }}
                    onCreateOption={(inputValue) => {
                      setUserAddedCompany(inputValue)
                      field.onChange(inputValue)
                      selectCompany(inputValue)
                    }}
                    classNames={{
                      control: () =>
                        'flex w-full rounded-md border border-input bg-background py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                    }}
                    isDisabled={form.formState.isSubmitting}
                    isClearable
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="jobField"
            render={({ field }) => (
              <FormItem
                id="field-jobField"
                className={getFieldHighlightClasses('jobField')}
              >
                <FormLabel className="hidden">Job Title</FormLabel>
                <FormControl>
                  <CreatableSelect
                    isDisabled={form.formState.isSubmitting}
                    placeholder="Job Title / Field of Expertise"
                    options={jobs?.map((job) => ({
                      value: job,
                      label: job,
                    }))}
                    value={
                      jobs
                        ?.filter((job: string) => job === field.value)
                        .map((job: string) => ({
                          value: job,
                          label: job,
                        }))[0]
                    }
                    onChange={(option) => {
                      field.onChange(option?.value)
                      markFieldAsInteracted('jobField')
                      if (option) {
                        selectJob(option.value)
                      }
                    }}
                    onCreateOption={(inputValue) => {
                      console.log('onCreateOption', inputValue)
                      setUserAddedJob(inputValue)
                      field.onChange(inputValue)
                      markFieldAsInteracted('jobField')
                      selectJob(inputValue)
                    }}
                    classNames={{
                      control: () =>
                        'flex w-full rounded-md border border-input bg-background py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                    }}
                    isClearable
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <p className="text-xs font-body text-body/70 md:mr-28 mt-2">
          When you start typing, we'll suggest options that are already in our
          system.{' '}
          <span className="font-bold">
            Selecting existing options will help with accuracy
          </span>
          , and, you won't have to type as much nor worry about spelling errors!
        </p>
        <p className="text-xs font-body text-body/70 md:mr-28 mt-1">
          EX. I am a multi-modality technician and I work for Banner Health at
          the hospital in the East Valley. I work closely with doctors, nurses,
          patients, and even machines!
        </p>

        <div className="flex flex-col mt-8">
          <FormLabel>Are You Fluent In Languages Besides English?</FormLabel>
          <p className="font-body text-sm mt-1">
            In other words, would you be comfortable speaking to an entire class
            primarily in a non-English language? Choose all that apply.
          </p>
          <FormField
            control={form.control}
            name="secondaryLanguages"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="hidden">Secondary Languages</FormLabel>
                <FormControl>
                  <CreatableSelect
                    isDisabled={form.formState.isSubmitting}
                    isMulti
                    isClearable={false}
                    placeholder="Select or create languages"
                    options={languages.map((language) => ({
                      value: language,
                      label: language,
                    }))}
                    value={
                      field.value?.map((language) => ({
                        value: language,
                        label: language,
                      })) || []
                    }
                    onChange={(selectedOptions) => {
                      field.onChange(
                        selectedOptions.map((option) => option.value)
                      )
                    }}
                    onCreateOption={(inputValue) => {
                      field.onChange([...(field.value || []), inputValue])
                    }}
                    classNames={{
                      control: () =>
                        'flex w-full rounded-md border border-input bg-background py-1 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <p className="text-xs font-body text-body/70 md:mr-28 mt-1">
            When you start typing, we'll suggest options that are already in our
            system. Selecting existing options will help with accuracy, and, you
            won't have to type as much nor worry about spelling errors!
          </p>
        </div>

        <div className="flex flex-col mt-8">
          <FormLabel className="mt-8">
            Tell Us a Little About Yourself
          </FormLabel>
          <p className="font-body text-sm mt-3">
            Tell us about you, your work, and experiences that students could
            learn from. We'll use this information to help write a draft of your
            bio that{' '}
            <span className="font-bold">you will be able to edit.</span>
          </p>
          <p className="font-body text-sm text-body/70 my-3">
            EX. I have been working in the field of radiology for 7 years. I
            started as an X-ray technician and completed studies to become a
            multi-modality technician, so I can work with MRI's and CT's! I am
            excited to talk to students about the professional and academic
            skills I use in my job and my career pathway. In my freetime, I
            enjoy working on cars and playing music with my friends.
          </p>
          <FormField
            control={form.control}
            name="about"
            render={({ field }) => (
              <FormItem
                id="field-about"
                className={clsx(
                  'w-full col-span-3',
                  getFieldHighlightClasses('about')
                )}
              >
                <FormLabel className="hidden">About</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    onChange={(e) => {
                      field.onChange(e)
                      markFieldAsInteracted('about')
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {!hideNavigation ? (
          <div className="flex-col flex mt-12 w-full">
            <Button
              type="submit"
              disabled={form.formState.isSubmitting}
              variant="default"
              className="mx-auto uppercase"
            >
              {form.formState.isSubmitting ? (
                <Indicators.Spinner
                  size="lg"
                  className="mt-auto mr-2 mb-auto ml-auto"
                />
              ) : null}
              <span>Next: Areas of Expertise &gt;</span>
            </Button>
            <Button
              type="button"
              variant="link"
              className="text-actionable underline text-sm"
              onClick={onSaveDraft}
            >
              <span>Save Draft and Finish Later</span>
            </Button>
          </div>
        ) : (
          <div className="flex-col flex mt-8 w-full">
            <Button
              type="submit"
              disabled={form.formState.isSubmitting}
              variant="default"
              className="ml-auto"
            >
              {form.formState.isSubmitting ? (
                <Indicators.Spinner
                  size="lg"
                  className="mt-auto mr-2 mb-auto ml-auto"
                />
              ) : null}
              <span>{editMode ? 'Update Profile' : 'Save Changes'}</span>
            </Button>
          </div>
        )}
      </form>
    </Form>
  )
}

export const ProfessionalStepOne = FormComponent
